import {
  buildRankedMatches,
  normalizeSearchText,
  resolveRankedMatch,
  scoreEntityCandidate,
  scoreTextMatch
} from './entityResolution.js'
import { getAliasesAcrossEntityTypes } from './entityAliasRegistry.js'

export function normalizeVaultName(value = '') {
  return normalizeSearchText(value)
    .replace(/\b(?:my|our|the|a|an)\b/g, ' ')
    .replace(/\bvault\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function listAvailableVaultNames(vaults = []) {
  return (Array.isArray(vaults) ? vaults : [])
    .map((vault) => vault?.name || vault?.title || '')
    .filter(Boolean)
}

export function normalizeFolderPath(value = '') {
  return String(value || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/{2,}/g, '/')
}

export function addFolderPathWithParents(target, path) {
  const normalized = normalizeFolderPath(path)
  if (!normalized || !target?.add) return
  const parts = normalized.split('/').filter(Boolean)
  for (let index = 1; index <= parts.length; index += 1) {
    target.add(parts.slice(0, index).join('/'))
  }
}

export function buildVaultFolders(folderMeta = {}, notes = []) {
  const folders = new Set()
  for (const folderPath of Object.keys(folderMeta || {})) {
    addFolderPathWithParents(folders, folderPath)
  }
  for (const note of Array.isArray(notes) ? notes : []) {
    addFolderPathWithParents(folders, note?.folder)
  }
  return Array.from(folders).sort((left, right) => left.localeCompare(right))
}

export function buildTopLevelFolders(folderPaths = []) {
  return Array.from(
    new Set(
      (Array.isArray(folderPaths) ? folderPaths : [])
        .map((path) => normalizeFolderPath(path).split('/')[0] || '')
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right))
}

export function resolveVaultByName(vaults = [], name = '') {
  const target = normalizeVaultName(name)
  const entries = (Array.isArray(vaults) ? vaults : [])
    .map((vault) => ({
      ...vault,
      normalized: normalizeVaultName(vault?.name || vault?.title || '')
    }))
    .filter((entry) => entry.normalized)

  if (!target) {
    return { vault: null, ambiguous: false, matches: [], status: 'none', requestedVaultName: null }
  }

  const exactMatches = entries.filter((entry) => entry.normalized === target)
  if (exactMatches.length === 1) {
    return {
      vault: exactMatches[0],
      ambiguous: false,
      matches: [exactMatches[0]],
      status: 'exact',
      requestedVaultName: name
    }
  }
  if (exactMatches.length > 1) {
    return {
      vault: null,
      ambiguous: true,
      matches: exactMatches,
      status: 'ambiguous',
      requestedVaultName: name
    }
  }

  const partialMatches = entries.filter(
    (entry) => entry.normalized.includes(target) || target.includes(entry.normalized)
  )
  if (partialMatches.length === 1) {
    return {
      vault: partialMatches[0],
      ambiguous: false,
      matches: [partialMatches[0]],
      status: 'partial',
      requestedVaultName: name
    }
  }
  if (partialMatches.length > 1) {
    return {
      vault: null,
      ambiguous: true,
      matches: partialMatches,
      status: 'ambiguous',
      requestedVaultName: name
    }
  }

  const scored = buildRankedMatches(entries, target, {
    limit: 5,
    mapTexts: (entry) => ({
      primaryText: entry.normalized
    }),
    mapResult: ({ item, score }) => ({
      ...item,
      score
    }),
    sortLabel: (entry) => entry.normalized
  })

  if (!scored.length) {
    return {
      vault: null,
      ambiguous: false,
      matches: [],
      status: 'not_found',
      requestedVaultName: name
    }
  }

  const resolved = resolveRankedMatch(scored, {
    minScore: 120,
    minGap: 25,
    minSingleScore: 120
  })

  if (resolved.status === 'single') {
    return {
      vault: resolved.match,
      ambiguous: false,
      matches: [resolved.match],
      status: 'fuzzy',
      requestedVaultName: name
    }
  }

  return {
    vault: null,
    ambiguous: true,
    matches: resolved.matches,
    status: 'ambiguous',
    requestedVaultName: name
  }
}

export function scoreNoteMatch(query = '', note = {}) {
  return scoreEntityCandidate(query, {
    primaryText: note?.title || '',
    aliases: listNoteAliases(note),
    secondaryTexts: [note?.content || '']
  })
}

function parseAliases(value) {
  if (Array.isArray(value)) return value.map((entry) => String(entry || '').trim()).filter(Boolean)
  if (typeof value === 'string') {
    return value
      .split(/[;,]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
  }
  return []
}

export function listNoteAliases(note = {}) {
  const frontmatter = note?.frontmatter && typeof note.frontmatter === 'object' ? note.frontmatter : {}
  const entity = frontmatter?.entity && typeof frontmatter.entity === 'object' ? frontmatter.entity : {}
  const aliases = [
    ...parseAliases(note?.aliases),
    ...parseAliases(frontmatter?.aliases),
    ...parseAliases(frontmatter?.alias),
    ...parseAliases(entity?.aliases),
    ...parseAliases(entity?.alias),
    entity?.label,
    frontmatter?.entityLabel
  ]

  const registryAliases = getAliasesAcrossEntityTypes(note?.title, note?.slug, entity?.id, entity?.label)

  return Array.from(
    new Set(
      [...aliases, ...registryAliases]
        .map((entry) => String(entry || '').trim())
        .filter(Boolean)
    )
  )
}

export function buildNoteMatches(pool = [], searchQuery = '', options = {}) {
  const limit = Number(options?.limit || 5)
  const excerptLength = Number(options?.excerptLength || 200)

  return buildRankedMatches(pool, searchQuery, {
    limit,
    mapTexts: (note) => ({
      primaryText: note?.title || '',
      aliases: listNoteAliases(note),
      secondaryTexts: [note?.content || '']
    }),
    mapResult: ({ item: note, score }) => ({
      id: note.id,
      title: note.title || '',
      excerpt: String(note.content || '').slice(0, excerptLength),
      campaignId: note.campaignId || note.campaign || null,
      vaultId: note.vaultId || null,
      vaultName: note.vaultName || null,
      source: note.vaultId ? 'knowledgebase' : note.source || 'campaign',
      score
    }),
    sortLabel: (note) => note?.title || note?.id || ''
  })
}

export function resolveNoteMatch(matches = [], options = {}) {
  const resolved = resolveRankedMatch(matches, options)
  return {
    status: resolved.status,
    note: resolved.match,
    matches: resolved.matches
  }
}

export function formatNoteChoice(note = {}) {
  const title = String(note?.title || 'Untitled').trim()
  const vaultName = String(note?.vaultName || '').trim()
  return vaultName ? `"${title}" in "${vaultName}"` : `"${title}"`
}

export function normalizeUpdateMode(value = '') {
  return String(value || '').toLowerCase() === 'replace' ? 'replace' : 'append'
}

export function parseNoteUpdateRequest(args = {}) {
  const appendText = typeof args?.append === 'string' ? String(args.append) : undefined
  const replaceText = typeof args?.content === 'string' ? String(args.content) : undefined
  const titlePatch = typeof args?.title === 'string' ? String(args.title).slice(0, 200) : null
  const mode = normalizeUpdateMode(args?.mode)
  const hasContentInput = typeof appendText === 'string' || typeof replaceText === 'string'

  return {
    mode,
    appendText,
    replaceText,
    titlePatch,
    hasContentInput
  }
}

export function buildNoteUpdateFields({
  existingContent = '',
  mode = 'append',
  appendText,
  replaceText,
  titlePatch = null
} = {}) {
  const updates = {}
  if (titlePatch) updates.title = titlePatch

  if (typeof appendText === 'string' || typeof replaceText === 'string') {
    const chunk = appendText ?? replaceText ?? ''
    updates.content =
      mode === 'replace' ? chunk : existingContent ? `${existingContent}\n${chunk}` : chunk
  }

  return updates
}

const noteResolution = {
  buildNoteMatches,
  buildNoteUpdateFields,
  buildTopLevelFolders,
  buildVaultFolders,
  formatNoteChoice,
  listAvailableVaultNames,
  listNoteAliases,
  normalizeFolderPath,
  normalizeSearchText,
  normalizeUpdateMode,
  normalizeVaultName,
  parseNoteUpdateRequest,
  resolveNoteMatch,
  resolveVaultByName,
  scoreNoteMatch,
  scoreTextMatch
}

export default noteResolution
